import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { useNavigate, Link } from "react-router-dom";

const CHROME_WEBSTORE_URL =
  "https://chromewebstore.google.com/detail/vrepop/depkoiepmnjkkbpcimmfhfndjjbbpkbl?hl=fr";
const TIKTOK_VIDEO_URL =
  "https://www.tiktok.com/@vrepop/video/7586019750396955926";
const TIKTOK_PLAYER_URL =
  "https://www.tiktok.com/player/v1/7586019750396955926?controls=1&progress_bar=1&play_button=1&music_info=0&description=0&rel=0&autoplay=0";

const features = [
  {
    icon: "♻️",
    title: "Republication automatique",
    text: "V-repop recrée votre annonce puis supprime l’ancienne une fois la nouvelle publiée.",
  },
  {
    icon: "📸",
    title: "Photos et informations conservées",
    text: "Titre, description, photos, prix, catégorie, taille, état et autres informations sont recopiés.",
  },
  {
    icon: "🛠️",
    title: "Modifications personnalisées",
    text: "Choisissez les changements à appliquer avant de faire apparaître le bouton Republier.",
  },
  {
    icon: "📱",
    title: "Sur ordinateur et Android",
    text: "Utilisez V-repop sur Chrome depuis un ordinateur ou avec Lemur Browser sur Android.",
  },
];

const steps = [
  {
    number: "01",
    title: "Installez V-repop",
    text: "Ajoutez l’extension depuis le Chrome Web Store. Sur Android, commencez par installer Lemur Browser.",
  },
  {
    number: "02",
    title: "Ouvrez votre dressing",
    text: "Connectez-vous normalement à Vinted puis ouvrez la page qui affiche toutes vos annonces.",
  },
  {
    number: "03",
    title: "Choisissez vos réglages",
    text: "Le widget V-repop apparaît en bas de la page. Connectez-vous et sélectionnez vos modifications.",
  },
  {
    number: "04",
    title: "Cliquez sur Republier",
    text: "Le bouton apparaît sur vos annonces. V-repop prend ensuite en charge toute la republication.",
  },
];

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loginOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setLoginOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [loginOpen]);

  const openLogin = () => {
    setError("");
    setLoginOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError("Merci de remplir tous les champs");
      setLoading(false);
      return;
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (signInError) {
      if (signInError.message?.toLowerCase().includes("email not confirmed")) {
        setError(
          "Votre email n’est pas encore confirmé. Vérifiez votre boîte mail ou vos spams puis réessayez."
        );
      } else {
        setError(signInError.message || "Email ou mot de passe incorrect.");
      }
      return;
    }

    navigate("/dashboard");
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError("Renseignez d’abord votre email dans le champ.");
      return;
    }

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(
      email,
      {
        redirectTo: window.location.origin + "/reset",
      }
    );

    if (resetError) {
      setError("Erreur lors de l’envoi du mail : " + resetError.message);
    } else {
      setError("");
      alert("Un email de réinitialisation a été envoyé si cet email existe.");
    }
  };

  return (
    <div className="vrepop-landing min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a href="#accueil" className="flex items-center gap-3" aria-label="Accueil V-repop">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-xl font-black text-white shadow-sm">
              V
            </span>
            <span className="text-xl font-black tracking-tight">
              V-<span className="text-blue-600">repop</span>
            </span>
          </a>

          <nav className="hidden items-center gap-7 text-sm font-semibold text-slate-600 lg:flex">
            <a href="#fonctionnement" className="transition hover:text-blue-600">
              Comment ça marche
            </a>
            <a href="#fonctionnalites" className="transition hover:text-blue-600">
              Fonctionnalités
            </a>
            <a href="#installation" className="transition hover:text-blue-600">
              Installation
            </a>
            <a href="#tarif" className="transition hover:text-blue-600">
              Tarif
            </a>
            <a href="/faq.html" className="transition hover:text-blue-600">
              FAQ
            </a>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={openLogin}
              className="rounded-xl px-3 py-2 text-sm font-bold text-blue-700 transition hover:bg-blue-50 sm:px-4"
            >
              Se connecter
            </button>
            <Link
              to="/register"
              className="rounded-xl bg-blue-600 px-3 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 sm:px-5 sm:py-3"
            >
              Créer un compte
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section
          id="accueil"
          className="vrepop-grid-bg scroll-mt-24 overflow-hidden border-b border-slate-100"
        >
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-[1.08fr_0.92fr] lg:px-8 lg:py-24">
            <div className="max-w-2xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700">
                <span aria-hidden="true">✨</span>
                La republication Vinted devient simple
              </div>

              <h1 className="text-4xl font-black leading-tight tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
                Redonnez de la visibilité à vos annonces{" "}
                <span className="vrepop-text-gradient">en un clic</span>
              </h1>

              <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600 sm:text-xl">
                V-repop recopie vos photos et vos informations, publie la nouvelle annonce puis
                supprime l’ancienne. Vous gagnez du temps sans refaire votre annonce manuellement.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-4 text-base font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  Commencer avec V-repop
                  <span className="ml-2" aria-hidden="true">→</span>
                </Link>
                <a
                  href="#demonstration"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-6 py-4 text-base font-black text-slate-800 transition hover:border-blue-300 hover:bg-blue-50"
                >
                  <span className="mr-2" aria-hidden="true">▶</span>
                  Voir la démonstration
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-slate-600">
                <span className="flex items-center gap-2"><b className="text-green-600">✓</b> PC et Android</span>
                <span className="flex items-center gap-2"><b className="text-green-600">✓</b> Jusqu’à 3 000 republications</span>
                <span className="flex items-center gap-2"><b className="text-green-600">✓</b> Seulement 2 €</span>
              </div>
            </div>

            <div id="demonstration" className="scroll-mt-28 lg:justify-self-center">
              <div className="mx-auto max-w-sm rounded-3xl border border-slate-200 bg-white p-4 shadow-2xl shadow-slate-900/10 sm:p-6">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-black text-slate-950">V-repop en situation réelle</p>
                    <p className="mt-1 text-xs text-slate-500">Démonstration depuis un téléphone</p>
                  </div>
                  <span className="rounded-full bg-pink-50 px-3 py-1 text-xs font-black text-pink-600">
                    VIDÉO
                  </span>
                </div>

                <div className="vrepop-phone-shell mx-auto">
                  <iframe
                    src={TIKTOK_PLAYER_URL}
                    title="Démonstration de V-repop sur téléphone"
                    className="vrepop-tiktok-player"
                    loading="lazy"
                    allow="accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                </div>

                <a
                  href={TIKTOK_VIDEO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center justify-center text-sm font-bold text-blue-600 hover:underline"
                >
                  Regarder directement sur TikTok
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-100 bg-white py-8">
          <div className="mx-auto grid max-w-6xl grid-cols-1 divide-y divide-slate-200 px-4 text-center sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-6">
            <div className="px-4 py-4">
              <p className="font-black text-slate-950">Extension Chrome Web Store</p>
              <p className="mt-1 text-sm text-slate-500">Installation simple et rapide</p>
            </div>
            <div className="px-4 py-4">
              <p className="font-black text-slate-950">Aucun identifiant Vinted collecté</p>
              <p className="mt-1 text-sm text-slate-500">V-repop agit depuis votre navigateur</p>
            </div>
            <div className="px-4 py-4">
              <p className="font-black text-slate-950">Un tarif accessible</p>
              <p className="mt-1 text-sm text-slate-500">2 € pour 14 jours d’accès</p>
            </div>
          </div>
        </section>

        <section id="fonctionnement" className="scroll-mt-24 bg-slate-50 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">Comment ça marche ?</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                Quatre étapes, puis V-repop travaille pour vous
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Vous restez connecté à votre propre compte Vinted dans votre navigateur.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {steps.map((step) => (
                <article key={step.number} className="vrepop-soft-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <span className="text-3xl font-black text-blue-200">{step.number}</span>
                  <h3 className="mt-4 text-lg font-black text-slate-950">{step.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="fonctionnalites" className="scroll-mt-24 bg-white py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
              <div className="lg:sticky lg:top-28">
                <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">L’essentiel, sans complication</p>
                <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                  Tout ce qu’il faut pour republier plus rapidement
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  V-repop se concentre sur une mission : vous éviter de recréer manuellement vos anciennes annonces.
                </p>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                {features.map((feature) => (
                  <article key={feature.title} className="vrepop-soft-card rounded-2xl border border-slate-200 bg-slate-50 p-6">
                    <span className="text-3xl" aria-hidden="true">{feature.icon}</span>
                    <h3 className="mt-4 text-lg font-black text-slate-950">{feature.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-slate-600">{feature.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="installation" className="scroll-mt-24 border-y border-slate-100 bg-slate-50 py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">Installation guidée</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                Choisissez votre appareil
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Les guides détaillés sont accessibles avant même de créer votre compte.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <article className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-3xl" aria-hidden="true">🖥️</div>
                <h3 className="mt-6 text-2xl font-black text-slate-950">Sur ordinateur</h3>
                <p className="mt-3 leading-7 text-slate-600">
                  Installez l’extension dans Google Chrome puis ouvrez votre dressing Vinted.
                </p>
                <Link to="/install-pc" className="mt-6 inline-flex font-black text-blue-600 hover:underline">
                  Voir le guide PC <span className="ml-2" aria-hidden="true">→</span>
                </Link>
              </article>

              <article className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-3xl" aria-hidden="true">📱</div>
                <h3 className="mt-6 text-2xl font-black text-slate-950">Sur Android</h3>
                <p className="mt-3 leading-7 text-slate-600">
                  Installez Lemur Browser, ajoutez V-repop puis utilisez Vinted directement dans le navigateur.
                </p>
                <Link to="/install-android" className="mt-6 inline-flex font-black text-blue-600 hover:underline">
                  Voir le guide Android <span className="ml-2" aria-hidden="true">→</span>
                </Link>
              </article>
            </div>

            <div className="mt-8 text-center">
              <a
                href={CHROME_WEBSTORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-6 py-3 font-black text-blue-700 transition hover:bg-blue-100"
              >
                Ouvrir V-repop sur le Chrome Web Store
              </a>
            </div>
          </div>
        </section>

        <section id="tarif" className="scroll-mt-24 bg-blue-700 py-20 text-white sm:py-24">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_0.8fr] lg:px-8">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-200">Un tarif simple</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
                Pas besoin d’une offre compliquée pour republier vos annonces
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-blue-100">
                Le plan Royal donne accès à toutes les fonctions de republication. Le quota est renouvelé à chaque nouvelle période.
              </p>
            </div>

            <div className="rounded-3xl bg-white p-7 text-slate-950 shadow-2xl sm:p-9">
              <p className="text-sm font-black uppercase tracking-wider text-blue-600">Plan Royal</p>
              <div className="mt-3 flex items-end gap-2">
                <span className="text-5xl font-black">2 €</span>
                <span className="pb-1 text-slate-500">/ 14 jours</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm font-semibold text-slate-700">
                <li className="flex gap-3"><span className="text-green-600">✓</span> Jusqu’à 3 000 republications par période</li>
                <li className="flex gap-3"><span className="text-green-600">✓</span> Toutes les fonctions de V-repop</li>
                <li className="flex gap-3"><span className="text-green-600">✓</span> Résiliation possible à tout moment</li>
              </ul>
              <Link
                to="/register"
                className="mt-7 flex w-full items-center justify-center rounded-xl bg-blue-600 px-6 py-4 font-black text-white transition hover:bg-blue-700"
              >
                Choisir mon offre
              </Link>
              <p className="mt-4 text-center text-xs leading-5 text-slate-500">
                Fin de l’accès au premier des deux : quota atteint ou expiration des 14 jours. Pas de report.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-6 px-4 text-center sm:px-6 lg:flex-row lg:text-left">
            <div>
              <h2 className="text-2xl font-black text-slate-950">Prêt à redonner de la visibilité à vos annonces ?</h2>
              <p className="mt-2 text-slate-600">Créez votre compte puis installez V-repop sur votre appareil.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/register" className="rounded-xl bg-blue-600 px-6 py-3 font-black text-white hover:bg-blue-700">
                Créer mon compte
              </Link>
              <button
                type="button"
                onClick={openLogin}
                className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-black text-slate-800 hover:bg-slate-50"
              >
                J’ai déjà un compte
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-slate-950 py-10 text-slate-300">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 text-center sm:px-6 md:flex-row md:text-left lg:px-8">
          <div>
            <p className="font-black text-white">V-repop</p>
            <p className="mt-1 text-sm text-slate-400">Republication Vinted automatique sur ordinateur et Android.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm font-semibold">
            <a href="/faq.html" className="hover:text-white">FAQ</a>
            <a href="/cgv" target="_blank" rel="noopener noreferrer" className="hover:text-white">CGV</a>
            <a href="/cgu" target="_blank" rel="noopener noreferrer" className="hover:text-white">CGU</a>
            <button type="button" onClick={openLogin} className="font-semibold hover:text-white">Se connecter</button>
          </div>
        </div>
      </footer>

      {loginOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLoginOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-title"
            className="vrepop-login-modal relative w-full max-w-md rounded-3xl bg-white p-7 shadow-2xl sm:p-9"
          >
            <button
              type="button"
              onClick={() => setLoginOpen(false)}
              aria-label="Fermer la fenêtre de connexion"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xl text-slate-600 transition hover:bg-slate-200"
            >
              ×
            </button>

            <div className="mb-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-xl font-black text-white">V</span>
              <h2 id="login-title" className="mt-5 text-2xl font-black text-slate-950">Bon retour sur V-repop</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">Connectez-vous pour accéder à votre dashboard et à votre quota.</p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">Adresse email</span>
                <input
                  type="email"
                  placeholder="vous@exemple.fr"
                  autoComplete="email"
                  autoFocus
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">Mot de passe</span>
                <input
                  type="password"
                  placeholder="Votre mot de passe"
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>

              {error && (
                <div className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full rounded-xl bg-blue-600 py-3 font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loading}
              >
                {loading ? "Connexion..." : "Se connecter"}
              </button>
            </form>

            <div className="mt-4 flex items-center justify-between gap-4 text-sm">
              <button
                type="button"
                onClick={handleResetPassword}
                className="font-semibold text-blue-600 hover:underline"
              >
                Mot de passe oublié
              </button>
              <Link to="/register" className="font-semibold text-blue-600 hover:underline">
                Créer un compte
              </Link>
            </div>

            <p className="mt-5 border-t border-slate-100 pt-4 text-xs leading-5 text-slate-500">
              Après une inscription, confirmez votre adresse depuis l’email reçu avant de vous connecter.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
